import React from "react";
import {FaUpRightFromSquare} from "react-icons/fa6";
import {MarkdownStyledComment} from "./style";

export function formatDate(dateString: string): string {
    const date = new Date(dateString);
    const today = new Date();

    // Normalize times to start of the day for accurate comparisons
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);

    const diffInMs = today.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) {
        return "Today";
    } else if (diffInDays < 14) {
        return `${diffInDays} days ago`;
    } else if (diffInDays < 8 * 7) {
        return `${Math.floor(diffInDays / 7)} weeks ago`;
    } else if (diffInDays < 24 * 30) {
        return `${Math.floor(diffInDays / 30)} months ago`;
    } else {
        return `${Math.floor(diffInDays / 365)} years ago`;
    }
}

export function capitalizeFirstLetter(val?: string | any): string {
    if (!val) return '';
    return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}

export const LinkNew = ({ to, disableIcon = false, children }: {
  to: string;
  disableIcon?: boolean;
  children: React.ReactNode;
}) => {
  return (
    <a href={to} target="_blank" rel="noopener noreferrer">
      {children} {!disableIcon && <FaUpRightFromSquare />}
    </a>
  );
};

export const popOutSVG = '<svg class="link-like" stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 512 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M352 0c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9L370.7 96 201.4 265.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L416 141.3l41.4 41.4c9.2 9.2 22.9 11.9 34.9 6.9s19.8-16.6 19.8-29.6V32c0-17.7-14.3-32-32-32H352zM80 32C35.8 32 0 67.8 0 112V432c0 44.2 35.8 80 80 80H400c44.2 0 80-35.8 80-80V320c0-17.7-14.3-32-32-32s-32 14.3-32 32V432c0 8.8-7.2 16-16 16H80c-8.8 0-16-7.2-16-16V112c0-8.8 7.2-16 16-16H192c17.7 0 32-14.3 32-32s-14.3-32-32-32H80z"></path></svg>'

// Escape HTML function to prevent dangerous interpretation
const escapeHtml = (str: string): string => {
  return str.replace(/[&<>"'/]/g, (match) => {
    const escapeMap: { [key: string]: string } = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
      "/": "&#x2F;",
    };
    return escapeMap[match];
  });
};

export const parseMarkdown = (markdown: string): string => {
  // Step 1: Escape any HTML content
  const escapedText = escapeHtml(markdown);

  // Step 2: Apply markdown transformations
  return escapedText
    // Code blocks ```code``` (multi-line)
    .replace(/```(?:\r?\n)?([\s\S]*?)(?:\r?\n)?```/g, "<div class='code'>$1</div>")

    // Bold **text** — no newlines, no space after ** or before **
    .replace(/\*\*(?!\s)([^\n*]+?)(?<!\s)\*\*/g, "<strong>$1</strong>")

    // Italic *text* — no newlines, no space after * or before *
    .replace(/(?<!\*)\*(?!\s)([^\n*]+?)(?<!\s)\*(?!\*)/g, "<em>$1</em>")

    // Inline code `text` — no newlines, no space after ` or before `
    .replace(/`(?!\s)([^\n`]+?)(?<!\s)`/g, "<span class='pre'>$1</span>")

    // Strikethrough ~text~
    .replace(/~(.*?)~/g, "<s>$1</s>")

    // Headings # -> <u><strong>
    .replace(/^(#{1,6}) (.*)$/gm, "<u><strong>$2</strong></u>")

    // Links [text](url)
    .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>' + popOutSVG)

    // Convert newlines to <br>
    .replace(/\n/g, "<br>")

    // Group sequential list items under a single <ul>
    .replace(/(?:<br>\* (.*?))+<br>/g, (match) => {
      const items = match.split('<br>* ').slice(1);
      return `<ul>${items.map(item => `<li>${item}</li>`).join('')}</ul>`;
    });
};



export const MarkdownToHtml: React.FC<{ text: string}> = ({ text = ''}) => {
  return (<MarkdownStyledComment dangerouslySetInnerHTML={{__html: (
      text.length
  ) ? (
      parseMarkdown(text)
  ) : (
      "<span style='color: #777'>Markdown preview...</span>"
  )}}/>);
};